// Scoring system — evaluates negotiation performance

export function calculateScore(result) {
  const {
    finalPrice, scenario, playerRole, timeUsed, timeLimit,
    totalRounds, bluffsUsed, bluffSuccesses, pressuresUsed,
    dealReached, opponentWalkedAway,
  } = result;

  if (!dealReached) {
    return {
      total: 0,
      dealValue: 0,
      efficiency: 0,
      technique: 0,
      grade: 'F',
      gradeColor: '#ff3366',
      summary: opponentWalkedAway
        ? 'The opponent walked away. No deal reached.'
        : 'You walked away. Sometimes that\'s the right call... but not this time.',
    };
  }

  const target = scenario.playerTarget;
  const listPrice = scenario.listPrice;
  const range = Math.abs(target - listPrice);
  const isBuyer = playerRole === 'buyer';

  // 1. Deal Value Score (0-500) — how close to your target?
  let dealValueRatio;
  if (isBuyer) {
    // Lower is better for buyer
    if (finalPrice <= target) dealValueRatio = 1.0;
    else if (finalPrice >= listPrice) dealValueRatio = 0;
    else dealValueRatio = 1 - (finalPrice - target) / range;
  } else {
    // Higher is better for seller
    if (finalPrice >= target) dealValueRatio = 1.0;
    else if (finalPrice <= listPrice) dealValueRatio = 0;
    else dealValueRatio = (finalPrice - listPrice) / range;
  }
  const dealValue = Math.round(Math.max(0, dealValueRatio) * 500);

  // 2. Efficiency Score (0-200) — time management
  const timeRatio = timeUsed / timeLimit;
  let efficiency;
  if (timeRatio < 0.3) efficiency = 200; // Quick closer
  else if (timeRatio < 0.6) efficiency = 170;
  else if (timeRatio < 0.8) efficiency = 120;
  else efficiency = Math.max(50, 200 * (1 - timeRatio));
  efficiency = Math.round(efficiency);

  // 3. Technique Score (0-300) — tactics and skill
  let technique = 100; // Base

  // Bluff skill
  if (bluffsUsed > 0) {
    const bluffRate = bluffSuccesses / bluffsUsed;
    technique += Math.round(bluffRate * 80);
    if (bluffsUsed > 3) technique -= 30; // Over-bluffing penalty
  }

  // Pressure usage
  if (pressuresUsed >= 1 && pressuresUsed <= 3) technique += 40;
  if (pressuresUsed > 4) technique -= 20;

  // Round efficiency
  if (totalRounds <= 4) technique += 50;
  else if (totalRounds <= 8) technique += 20;
  else if (totalRounds > 12) technique -= 30;

  technique = Math.max(0, Math.min(300, technique));

  const total = dealValue + efficiency + technique;

  // Grade
  let grade, gradeColor;
  if (total >= 900) { grade = 'S'; gradeColor = '#ffcc00'; }
  else if (total >= 800) { grade = 'A'; gradeColor = '#00ff88'; }
  else if (total >= 650) { grade = 'B'; gradeColor = '#00aaff'; }
  else if (total >= 500) { grade = 'C'; gradeColor = '#ff8800'; }
  else if (total >= 300) { grade = 'D'; gradeColor = '#ff5500'; }
  else { grade = 'F'; gradeColor = '#ff3366'; }

  // Summary
  let summary;
  if (total >= 900) summary = 'Masterful negotiation. You dominated every angle.';
  else if (total >= 800) summary = 'Excellent deal. Sharp tactics and great timing.';
  else if (total >= 650) summary = 'Solid performance. Room to improve on technique.';
  else if (total >= 500) summary = 'Decent deal, but you left value on the table.';
  else if (total >= 300) summary = 'Rough negotiation. The opponent had the upper hand.';
  else summary = 'Poor outcome. Study your opponent more carefully next time.';

  return { total, dealValue, efficiency, technique, grade, gradeColor, summary };
}

export function getStreakBonus(streak) {
  if (streak >= 5) return { multiplier: 1.5, label: '🔥 ON FIRE' };
  if (streak >= 3) return { multiplier: 1.25, label: '⚡ HOT STREAK' };
  return { multiplier: 1, label: '' };
}
